import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { getPostEmailConfirmPath } from '../lib/resumeOnlyMode'
import { supabase } from '../lib/supabase'

import { GenufitAuthBrand } from './GenufitAuthBrand'

function EmailConfirmSuccessCard({
  countdown,
  previewMode,
  onContinue,
}: {
  countdown: number
  previewMode: boolean
  onContinue: () => void
}) {
  const redirectTarget = 'onboarding'

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-subtle px-4" data-testid="email-confirm-page">
      <div className="flex w-full max-w-[400px] flex-col items-center gap-8">
        <GenufitAuthBrand />

        <div className="w-full rounded-lg bg-surface-default p-8 shadow-sm text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-brand-primary-subtle mb-6">
            <svg className="h-8 w-8 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="text-h2 font-semibold text-text-primary mb-3">Email confirmed</h2>
          <p className="text-body text-text-secondary mb-8 leading-relaxed">
            {previewMode
              ? `Preview mode: redirect to ${redirectTarget} is disabled.`
              : `Redirecting to ${redirectTarget} in ${countdown}s…`}
          </p>

          {!previewMode && (
            <div className="w-full bg-surface-muted rounded-full h-2 mb-6">
              <div
                className="bg-brand-primary h-2 rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${((5 - countdown) / 5) * 100}%` }}
              />
            </div>
          )}

          <button
            type="button"
            onClick={onContinue}
            className="w-full min-h-11 px-6 py-3 bg-brand-primary text-text-inverse rounded-lg text-small font-semibold hover:bg-brand-primary-hover transition-colors"
          >
            {previewMode
              ? 'Preview mode (redirect disabled)'
              : 'Continue now'}
          </button>
        </div>
      </div>
    </div>
  )
}

function EmailConfirm() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [countdown, setCountdown] = useState(5)

  const previewMode = searchParams.get('preview') === 'true'
  const postConfirmPath = getPostEmailConfirmPath(false)

  useEffect(() => {
    if ((status === 'loading' || status === 'success') && !previewMode) {
      setCountdown(5)

      const redirectTimer = setTimeout(() => {
        navigate(postConfirmPath, { replace: true })
      }, 5000)

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => {
        clearTimeout(redirectTimer)
        clearInterval(countdownInterval)
      }
    }

    if (previewMode && (status === 'loading' || status === 'success')) {
      setCountdown(5)
    }
  }, [status, navigate, previewMode, postConfirmPath])

  const handlePreviewMode = () => {
    setStatus('success')
    setCountdown(5)
  }

  const extractTokensFromUrl = () => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const accessToken = hashParams.get('access_token')
    const refreshToken = hashParams.get('refresh_token')
    const queryToken = searchParams.get('token')
    const queryType = searchParams.get('type')
    return { accessToken, refreshToken, queryToken, queryType }
  }

  const handleOAuthCallback = async (accessToken: string, refreshToken: string | null): Promise<boolean> => {
    try {
      const { data, error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken || '',
      })

      if (sessionError) {
        throw sessionError
      }

      if (data.session?.user) {
        navigate(getPostEmailConfirmPath(false), { replace: true })
        return true
      }
    } catch (hashError: unknown) {
      console.error('Hash token confirmation error:', hashError)
    }
    return false
  }

  const handleEmailConfirmationOTP = async (queryToken: string, queryType: string): Promise<boolean> => {
    try {
      const { data, error: otpError } = await supabase.auth.verifyOtp({
        token_hash: queryToken,
        type: queryType as 'signup' | 'email',
      })

      if (otpError) {
        throw otpError
      }

      if (data.user) {
        setStatus('success')
        return true
      }
    } catch (otpError: unknown) {
      console.error('OTP token confirmation error:', otpError)
    }
    return false
  }

  const handleConfirmationError = () => {
    setError('Invalid or expired confirmation link. Please check your email for a new confirmation link or try signing up again.')
    setStatus('error')
  }

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        if (searchParams.get('preview') === 'true') {
          handlePreviewMode()
          return
        }

        const { accessToken, refreshToken, queryToken, queryType } = extractTokensFromUrl()

        if (accessToken && !queryToken) {
          const success = await handleOAuthCallback(accessToken, refreshToken)
          if (success) return
        }

        if (queryToken && (queryType === 'signup' || queryType === 'email')) {
          const success = await handleEmailConfirmationOTP(queryToken, queryType)
          if (success) return
        }

        handleConfirmationError()
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : String(err)
        console.error('Email confirmation error:', errorMessage)
        setError(errorMessage || 'Failed to confirm email. Please try again or contact support.')
        setStatus('error')
      }
    }

    confirmEmail()
  }, [searchParams, navigate])

  const handleContinue = () => {
    if (!previewMode) {
      navigate(postConfirmPath, { replace: true })
    }
  }

  if (status === 'loading' || status === 'success') {
    return (
      <EmailConfirmSuccessCard
        countdown={countdown}
        previewMode={previewMode}
        onContinue={handleContinue}
      />
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-subtle px-4" data-testid="email-confirm-error">
        <div className="flex w-full max-w-[400px] flex-col items-center gap-8">
          <GenufitAuthBrand />

          <div className="w-full rounded-lg bg-surface-default p-8 shadow-sm text-center space-y-6">
            <h2 className="text-h2 font-semibold text-text-primary">Confirmation failed</h2>
            <div className="bg-danger-subtle border border-danger/20 text-danger-strong px-4 py-3 rounded-md text-small text-left">
              {error}
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full min-h-11 px-4 py-2.5 text-small font-medium text-text-primary bg-surface-muted rounded-md hover:bg-surface-subtle transition-colors"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default EmailConfirm
